import { Box, Panel, Stack, Text } from '@/ui';
import { settingsPanelSurfaceSx } from '@/ui/composites/settingsPageTypographySx';
import { ErrorOutline, HelpOutline } from '@/ui/primitives';
import { BlockOutlined, CheckCircleOutlineIcon } from '@/ui/primitives/icons';
import {
  buildProjectCsvValidationResultIconFrameSx,
  projectCsvValidationResultActionsSx,
  projectCsvValidationResultContentSx,
  projectCsvValidationResultFileLabelSx,
  projectCsvValidationResultFileNameSx,
  projectCsvValidationResultFileRowSx,
  projectCsvValidationResultIconSize,
  projectCsvValidationResultStatusBlockSx,
  projectCsvValidationResultStatusRowSx,
  projectCsvValidationResultStatusTextSx,
  projectCsvValidationResultTitleRowSx,
  projectCsvValidationResultTitleSx,
} from './ProjectCsvValidationResultPanel.styles';
import { ValidationMissingValuesSection } from './ValidationMissingValuesSection';
import type { ValidationPreviewData } from './validationDataChecks';
import type { ReactElement } from 'react';

type ProjectCsvValidationResultPanelProps = {
  hasValidatedFile: boolean;
  previewData: ValidationPreviewData;
  fileName?: string | null;
  container?: 'panel' | 'embedded';
  actions?: ReactElement | null;
};

type ValidationResultState = {
  label: string;
  tone: 'success' | 'error' | 'warning' | 'textSecondary';
  icon: ReactElement;
};

const requiredHeaderLabels = new Set(['Test', 'Status']);

const resolveValidationResultState = (
  hasValidatedFile: boolean,
  previewData: ValidationPreviewData,
): ValidationResultState => {
  if (!hasValidatedFile) {
    return {
      label: 'Unvalidated',
      tone: 'textSecondary',
      icon: <HelpOutline fontSize="small" sx={{ fontSize: projectCsvValidationResultIconSize }} />,
    };
  }

  const hasRequiredMissingHeaders = previewData.missingHeaderLabels.some((label) =>
    requiredHeaderLabels.has(label),
  );
  const hasInvalidValues =
    previewData.missingTestValues.length > 0 ||
    previewData.missingSuiteValues.length > 0 ||
    previewData.invalidStatusValues.length > 0 ||
    previewData.invalidDurationValues.length > 0 ||
    previewData.invalidTimestampValues.length > 0;

  if (hasRequiredMissingHeaders || hasInvalidValues) {
    return {
      label: 'Invalid',
      tone: 'error',
      icon: <ErrorOutline fontSize="small" sx={{ fontSize: projectCsvValidationResultIconSize }} />,
    };
  }

  if (previewData.missingHeaderLabels.length > 0) {
    return {
      label: 'Valid with missing optional headers',
      tone: 'warning',
      icon: (
        <BlockOutlined fontSize="small" sx={{ fontSize: projectCsvValidationResultIconSize }} />
      ),
    };
  }

  if (previewData.missingStatusLabels.length > 0) {
    return {
      label: 'Valid with undetected statuses',
      tone: 'warning',
      icon: <HelpOutline fontSize="small" sx={{ fontSize: projectCsvValidationResultIconSize }} />,
    };
  }

  return {
    label: 'Valid',
    tone: 'success',
    icon: (
      <CheckCircleOutlineIcon
        fontSize="small"
        sx={{ fontSize: projectCsvValidationResultIconSize }}
      />
    ),
  };
};

export const ProjectCsvValidationResultPanel = ({
  hasValidatedFile,
  previewData,
  fileName,
  container = 'panel',
  actions = null,
}: ProjectCsvValidationResultPanelProps): ReactElement => {
  const validationResultState = resolveValidationResultState(hasValidatedFile, previewData);
  const hasValidationDetails =
    previewData.invalidDurationValues.length > 0 ||
    previewData.invalidTimestampValues.length > 0 ||
    previewData.invalidStatusValues.length > 0 ||
    previewData.missingTestValues.length > 0 ||
    previewData.missingSuiteValues.length > 0 ||
    previewData.missingHeaderLabels.length > 0 ||
    previewData.missingStatusLabels.length > 0;
  const content = (
    <Stack gap="sm" sx={projectCsvValidationResultContentSx}>
      <Box sx={projectCsvValidationResultTitleRowSx}>
        <Stack gap="xs">
          <Text component="span" role="body" sx={projectCsvValidationResultTitleSx}>
            Validation Result
          </Text>
        </Stack>
        {actions ? <Box sx={projectCsvValidationResultActionsSx}>{actions}</Box> : null}
      </Box>
      <Box sx={projectCsvValidationResultStatusRowSx}>
        <Stack gap="none" sx={projectCsvValidationResultFileRowSx}>
          <Text role="body" sx={projectCsvValidationResultFileLabelSx}>
            File
          </Text>
          <Text role="body" sx={projectCsvValidationResultFileNameSx}>
            {fileName ?? '-'}
          </Text>
        </Stack>
        <Stack gap="none" sx={projectCsvValidationResultStatusBlockSx}>
          <Text role="body" sx={projectCsvValidationResultFileLabelSx}>
            Status
          </Text>
          <Text role="body" color="textSecondary" sx={projectCsvValidationResultStatusTextSx}>
            <Text
              component="span"
              role="body"
              sx={buildProjectCsvValidationResultIconFrameSx(validationResultState.tone)}
            >
              {validationResultState.icon}
            </Text>
            {validationResultState.label}
          </Text>
        </Stack>
      </Box>
      {hasValidatedFile && hasValidationDetails ? (
        <Stack gap="md">
          <ValidationMissingValuesSection
            title="Invalid Durations"
            values={previewData.invalidDurationValues}
          />
          <ValidationMissingValuesSection
            title="Invalid Statuses"
            values={previewData.invalidStatusValues}
          />
          <ValidationMissingValuesSection
            title="Invalid Timestamps"
            values={previewData.invalidTimestampValues}
          />
          <ValidationMissingValuesSection
            title="Missing Test Values"
            values={previewData.missingTestValues}
          />
          <ValidationMissingValuesSection
            title="Missing Suite Values"
            values={previewData.missingSuiteValues}
          />
          <ValidationMissingValuesSection
            title="Missing Headers"
            values={previewData.missingHeaderLabels}
          />
          <ValidationMissingValuesSection
            title="Undetected Statuses"
            values={previewData.missingStatusLabels}
          />
        </Stack>
      ) : null}
    </Stack>
  );

  if (container === 'embedded') {
    return <Box>{content}</Box>;
  }

  return (
    <Panel
      title="Validation Result"
      footerGap="xs"
      surfaceVariant="settings"
      sx={settingsPanelSurfaceSx}
    >
      {content}
    </Panel>
  );
};
